import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { ErrorService } from '../../../error';
import {
  CommissionModel,
  CommissionPreviewModel,
  CommissionReportModel,
  CommissionReportSalesPersonCommissionsDashboardModel,
  CommissionReportSalesPersonProductionDashboardModel,
  SalesPersonCommissionListModel,
  SalesPersonCommissionModel,
} from './types';
import { catchError } from 'rxjs/operators';
import { CrudType } from '../../../service/message/types';
import { MessageService } from '../../../service/message/message.service';
import { Filter } from '../../search/types';

@Injectable({
  providedIn: 'root',
})
export class CommissionApiService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly errorService: ErrorService,
    private readonly messageService: MessageService,
  ) {}

  getById = (id: number): Observable<CommissionModel> => {
    let parameters: string[];
    parameters = [id.toString()];
    let url = this.formatUrl(parameters);
    return this.httpClient.get<CommissionModel>(url).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  getCommissionsPreview = (): Observable<CommissionPreviewModel[]> => {
    let parameters: string[];
    parameters = ['preview'];
    let url = this.formatUrl(parameters);
    return this.httpClient.get<CommissionPreviewModel[]>(url).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  add = ({
    commissionPreviewModels,
    isTotalRowCountSelected,
  }: {
    commissionPreviewModels: CommissionPreviewModel[];
    isTotalRowCountSelected: boolean;
  }): Observable<CommissionPreviewModel[]> => {
    let parameters: string[];
    parameters = ['commissions'];
    let url = this.formatUrl(parameters);

    return this.httpClient
      .put<CommissionPreviewModel[]>(url, commissionPreviewModels, {
        observe: 'response',
      })
      .pipe(
        this.messageService.checkStatusCustomOperator(CrudType.create),
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  getCompanyCommissions = (
    filter: string,
  ): Observable<CommissionReportModel[]> => {
    let parameters: string[];
    parameters = ['companies'];

    let params = new HttpHeaders();
    if (filter) {
      params = params.append('filter', filter);
    }

    let url = this.formatUrl(parameters);
    return this.httpClient
      .get<CommissionReportModel[]>(url, { headers: params })
      .pipe(
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  getExcludedTaxAnnualFees = (
    filter: string,
  ): Observable<CommissionReportModel[]> => {
    let parameters: string[];
    parameters = ['annualfees'];

    let params = new HttpHeaders();
    if (filter) {
      params = params.append('filter', filter);
    }

    let url = this.formatUrl(parameters);
    return this.httpClient
      .get<CommissionReportModel[]>(url, { headers: params })
      .pipe(
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  getApplicationFeesReport = (
    filter: string,
  ): Observable<CommissionReportModel[]> => {
    let parameters: string[];
    parameters = ['applicationfees'];

    let params = new HttpHeaders();
    if (filter) {
      params = params.append('filter', filter);
    }

    let url = this.formatUrl(parameters);
    return this.httpClient
      .get<CommissionReportModel[]>(url, { headers: params })
      .pipe(
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  addSalesPersonCommissionPayment = (
    salesPersonCommissionListModel: SalesPersonCommissionListModel,
  ): Observable<boolean> => {
    let parameters: string[];
    parameters = ['commissions', 'salesperson', 'payment'];
    let url = this.formatUrl(parameters);
    return this.httpClient
      .post<boolean>(url, salesPersonCommissionListModel, {
        observe: 'response',
      })
      .pipe(
        this.messageService.checkStatusCustomOperator(CrudType.create),
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  deleteSalesPersonCommissions = (
    salesPersonCommissionListModel: SalesPersonCommissionListModel,
  ): Observable<boolean> => {
    let parameters: string[];
    parameters = ['commissions', 'salesperson'];
    let url = this.formatUrl(parameters);
    return this.httpClient
      .delete<boolean>(url, {
        body: salesPersonCommissionListModel,
        observe: 'response',
      })
      .pipe(
        this.messageService.checkStatusCustomOperator(CrudType.delete),
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  deleteSalesPersonCommissionPayment = (ids: number[]): Observable<boolean> => {
    let parameters: string[];
    parameters = ['commissions', 'salesperson', 'payment'];
    let url = this.formatUrl(parameters);
    return this.httpClient
      .delete<boolean>(url, {
        body: ids,
        observe: 'response',
      })
      .pipe(
        this.messageService.checkStatusCustomOperator(CrudType.delete),
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  getSalesPersonProductionDashboardData = (
    filters: Filter[],
  ): Observable<CommissionReportSalesPersonProductionDashboardModel> => {
    let parameters: string[];
    parameters = ['salesperson', 'production', 'dashboard'];

    let url = this.formatUrl(parameters);
    return this.httpClient
      .post<CommissionReportSalesPersonProductionDashboardModel>(url, filters)
      .pipe(
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  getSalesPersonCommissionsDashboardData = (
    filters: Filter[],
  ): Observable<CommissionReportSalesPersonCommissionsDashboardModel> => {
    let parameters: string[];
    parameters = ['salesperson', 'commissions', 'dashboard'];

    let url = this.formatUrl(parameters);
    return this.httpClient
      .post<CommissionReportSalesPersonCommissionsDashboardModel>(url, filters)
      .pipe(
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  formatUrl = (parameters: string[]): string => {
    var url = environment.comparanooapiurl;
    url += environment.features.commission.url;
    parameters.forEach((parameters) => {
      url += '/';
      url += parameters;
    });
    return url;
  };
}
